import type { FieldErrors, UseFormRegister } from 'react-hook-form'
import clsx from 'clsx'
import { sanitizeFieldName } from '@/utils/sanitizedLabel'
import { useState } from 'react'

type Props = {
	id: string
	label: string
	required: boolean
	formStyle: string
	colSpan?: string
	register: UseFormRegister<any>
	errors: FieldErrors
	className?: string
}

export default function DatePickerInput({
	id,
	label,
	required,
	formStyle,
	colSpan = '1',
	register,
	errors,
	className,
}: Props) {
	const fieldName = sanitizeFieldName(label)
	const [selectedDate, setSelectedDate] = useState('')

	const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (!e.target.value) {
			setSelectedDate('')
			return
		}

		// Create date with UTC time
		const date = new Date(e.target.value + 'T00:00:00Z')

		// Format date in US format considering UTC
		const formattedDate = new Intl.DateTimeFormat('en-US', {
			month: '2-digit',
			day: '2-digit',
			year: 'numeric',
			timeZone: 'UTC',
		}).format(date)

		setSelectedDate(formattedDate)
	}

	// Get today's date in YYYY-MM-DD format for min attribute
	const today = new Date().toISOString().split('T')[0]

	return (
		<div
			className={clsx(
				'form-component',
				{
					'2': 'colSpan2',
				}[colSpan],
				className
			)}
		>
			<div
				className={clsx('form-element-container relative', formStyle === 'line' && 'line-style')}
				onClick={() => {
					const input = document.getElementById(id) as HTMLInputElement
					if (input) {
						input.showPicker()
					}
				}}
			>
				<input
					id={id}
					type="date"
					lang="en"
					className={clsx(
						'date-picker w-full cursor-pointer opacity-0',
						selectedDate !== '' && 'selected-date'
					)}
					min={today}
					{...register(fieldName, {
						required: required && {
							value: true,
							message: `${label} is required`,
						},
						onChange: handleDateChange,
						setValueAs: (value) => {
							if (!value) return ''
							// Create date with UTC time for form submission
							const date = new Date(value + 'T00:00:00Z')
							return new Intl.DateTimeFormat('en-US', {
								month: '2-digit',
								day: '2-digit',
								year: 'numeric',
								timeZone: 'UTC',
							}).format(date)
						},
					})}
				/>
				<div className="absolute inset-0 flex items-center px-5 pointer-events-none text-current text-lg translate-y-2.5">
					{selectedDate}
				</div>
			</div>

			<label htmlFor={id}>{`${label} ${required ? '*' : ''}`}</label>

			{errors[fieldName] && (
				<span className="mt-1 text-red-500">{errors[fieldName]?.message?.toString()}</span>
			)}
		</div>
	)
}
